<template>
  <div>
    <v-tabs class="tabs" v-model="currentItem" left slider-color="white">
      <v-tab
        class="smaller-tab"
        :class="{ 'pl-0': idx === 0 }"
        v-for="(item, idx) in notExpiredPrices"
        :key="item.id"
      >
        <div class="tab-pricing-wrapper">
          <span>{{ checkExpired(item) }}{{ formatScheduleDate(item) }}</span>
          <v-btn
            v-if="isActiveTab(idx) && priceDetails"
            class="pkmn-button--no-padding"
            @click="editSchedule"
            text
          >
            <v-icon class="primary--text pa-0" right> mdi-pencil </v-icon>
          </v-btn>
          <v-tooltip
            color="rgba(0, 0, 0, 0)"
            v-if="isDraft(item)"
            bottom
            content-class=""
          >
            <template v-slot:activator="{ on }">
              <span class="warning-dot-outlined ml-2" v-on="on"></span>
            </template>
            <div class="tooltip-content">
              <span
                >Draft. Activate when all pricing rules are set. After that only
                the end date will be editable</span
              >
            </div>
            <div class="tooltip-arrow"></div>
          </v-tooltip>
        </div>
      </v-tab>

      <v-spacer></v-spacer>
      <v-menu v-if="expiredPrices.length" bottom left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            class="pkmn-button-small btn-past-schedule"
            v-bind="attrs"
            v-on="on"
          >
            Check past schedules
            <v-icon right> mdi-menu-down </v-icon>
          </v-btn>
        </template>

        <v-list class="grey lighten-3">
          <v-list-item
            v-for="item in expiredPrices"
            :key="item.id"
            @click="openExpiredSchedule(item)"
          >
            {{ formatScheduleDate(item) }}
          </v-list-item>
        </v-list>
      </v-menu>
    </v-tabs>

    <v-tabs-items class="tab-pricing-items" v-model="currentItem">
      <v-tab-item
        class="tab-item"
        v-for="item in notExpiredPrices"
        :key="item.id"
      >
        <v-divider class="mb-4" />
        <v-btn
          class="pkmn-button-small mr-2"
          outlined
          color="primary"
          @click="copyCurrentPrice"
        >
          Copy whole schedule 
        </v-btn>
        <v-btn
          class="pkmn-button-small"
          color="primary"
          v-if="item.status_id === 1"
          @click="pasteCurrentPrice"
        >
          Paste copied schedule 
        </v-btn>
        <tab-schedule
          ref="tabSchedule"
          :currencyId="currencyId"
          :weekday="currentWeekday"
          :periodic="currentPeriodic"
          :sequence="currentSequence"
          :sequencePeriod="sequencePricePeriod"
          :isActive="isActive(item)"
          :isExpired="isExpired(item)"
          :closeDataTableWeekdayPopup="closeDataTableWeekdayPopup"

          :showCopyPasteWeekday="auth.isParkmanAdmin()"
          :canPasteWeekday="isDraft(item)"
          :pasteWeekdayLoading="pasteState.weekday"
          
          @copyWeekday="copyWeekdayOnly"
          @pasteWeekday="pasteWeekdayOnly"

          :showCopyPasteSequence="true"
          :canPasteSequence="isDraft(item)"
          :pasteSequenceLoading="pasteState.sequence"
          @copySequence="copySequenceOnly"
          @pasteSequence="pasteSequenceOnly"

          :showCopyPasteSequencePeriod="true"
          :canPasteSequencePeriod="isDraft(item)"
          :pasteSequencePeriodLoading="pasteState.sequencePeriods"
          @copySequencePeriod="copySequencePeriodsOnly"
          @pasteSequencePeriod="pasteSequencePeriodsOnly"

          :showCopyPastePeriodic="true"
          :canPastePeriodic="isDraft(item)"
          :pastePeriodicLoading="pasteState.periodic"
          @copyPeriodic="copyPeriodicOnly"
          @pastePeriodic="pastePeriodicOnly"
          @addWeekDay="addWeekDay"
          @updateWeekDay="updateWeekDay"
          @deleteWeekDay="deleteWeekDay"
          @addSequence="addSequence"
          @updateSequence="updateSequence"
          @deleteSequence="deleteSequence"
          @addSequencePeriod="createSequencePricesPeriod"
          @deleteSequencePeriod="deleteSequencePeriod"
          @updateSequencePeriod="updateSequencePeriod"
          @addPeriodic="addPeriodic"
          @updatePeriodic="updatePeriodic"
          @deletePeriodic="deletePeriodic"
        />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import TabSchedule from '@/components/Locations/AddVisitorParking/Tab/TabPricingSchedule.vue'
import merchantHelpers from '@/helpers/merchant'
import moment from 'moment'
import auth from '@/auth'
import pricingClipboard from '@/services/pricingClipboard'
export default {
  props: {
    currencyId: {
      type: Number,
      default: 0
    },
    prices: {
      type: Array,
      default: () => []
    }
  },
  name: 'TabPricing',

  components: {
    TabSchedule
  },

  data: () => ({
    auth: auth,
    currentItem: 0,
    items: [],
    currentWeekday: [],
    currentSequence: [],
    currentPeriodic: [],
    sequencePricePeriod: [],
    priceDetails: null,
    expiredPrices: [],
    notExpiredPrices: [],
    closeDataTableWeekdayPopup: true,
    copiedSchedule: null,
    isPasting: false,
    copied: {
      weekday: null,
      sequence: null,
      periodic: null,
      sequencePeriods: null,
    },
    pasteState: {
      weekday: false,
      sequence: false,
      periodic: false,
      sequencePeriods: false,
    },
  }),
  methods: {
    async copyCurrentPrice() {
        const current = this.notExpiredPrices[this.currentItem]
        if (!current) {
          await this.$store.dispatch('setSystemMessage', {
            type: 'error',
            message: 'Please switch to an active schedule to copy.'
          })
          return
        }

        await this.getPricingDetails(current.id)
        await this.getSequencePricesPeriod()

        const weekdayGroups = this.groupWeekdays(this.priceDetails) || []
        const sequenceSteps = this.mapSequence(this.priceDetails) || []
        const periodicItems = this.mapPeriodic(this.priceDetails) || []
        const seqPeriods = this.sequencePricePeriod || []

        const payload = {
          weekdays: weekdayGroups.map(g => this.normalizeWeekdayForCreate(g)),
          sequenceSteps: sequenceSteps.map(s => this.normalizeSequenceStepForCreate(s)),
          periodic: periodicItems.map(p => ({ time_period: p.time_period, price: p.price })),
          sequencePeriods: seqPeriods.map(sp => this.normalizeSequencePeriodForCreate(sp)),
        }

        pricingClipboard.set('all', payload, {
          currencyId: this.currencyId,
          sourceScheduleId: current.id,
          kind: 'all',
        })

        await this.$store.dispatch('setSystemMessage', {
          type: 'success',
          message: 'Schedule copied.'
        })
    },
    async pasteCurrentPrice() {
      const target = this.notExpiredPrices[this.currentItem]
      if (!target || target.status_id !== 1) {
        await this.$store.dispatch('setSystemMessage', {
          type: 'error',
          message: 'Please switch to a draft schedule to paste.'
        })
        return
      }

      const copied = pricingClipboard.get('all')
      const meta = pricingClipboard.getMeta?.('all') || {}
      if (!copied) {
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: 'Nothing to paste. Copy an active schedule first.'
        })
        return
      }


      if (meta.currencyId && this.currencyId && meta.currencyId !== this.currencyId) {
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: 'Currencies differ between copied and target schedules.'
        })
      }

      this.isPasting = true
      const scheduleId = target.id
      try {
        if (copied.weekdays?.length) {
          await Promise.all(
            copied.weekdays.map(item =>
              merchantHelpers.createWeekdayPrices(
                this.$route.params.merchantId,
                scheduleId,
                item
              )
            )
          )
        }

        if (copied.sequenceSteps?.length) {
          for (const step of copied.sequenceSteps) {
            await merchantHelpers.createSequencePrices(
              this.$route.params.merchantId,
              scheduleId,
              step
            )
          }
        }

        await this.getPricingDetails(scheduleId)

        if (copied.periodic?.length) {
          await Promise.all(
            copied.periodic.map(item =>
              merchantHelpers.createPeriodicPrices(
                this.$route.params.merchantId,
                scheduleId,
                item
              )
            )
          )
        }

        if (copied.sequencePeriods?.length) {
          for (const period of copied.sequencePeriods) {
            await merchantHelpers.createSequencePricesPeriod(
              this.$route.params.merchantId,
              scheduleId,
              this.priceDetails.sequencePriceSteps?.price_sequence_id,
              period
            )
          }
        }

        await this.getPricingDetails(scheduleId)
        await this.getSequencePricesPeriod()

        await this.$store.dispatch('setSystemMessage', {
          type: 'success',
          message: 'Schedule pasted into draft.'
        })
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: this.$t('remember_to_update_admin_v2_settings')
        })
      } catch (e) {
        console.error(e)
        await this.$store.dispatch('setSystemMessage', {
          type: 'error',
          message: 'Could not paste schedule. Please try again.'
        })
      } finally {
        this.isPasting = false
      }
    },
    async copyWeekdayOnly() {
      const current = this.notExpiredPrices[this.currentItem]
      if (!current) return
      await this.getPricingDetails(current.id)

      const groups = this.groupWeekdays(this.priceDetails) || []
      const normalized = groups.map(g => this.normalizeWeekdayForCreate(g))

      pricingClipboard.set('weekday', normalized, {
        currencyId: this.currencyId,
        sourceScheduleId: current.id,
        kind: 'weekday',
      })
      await this.$store.dispatch('setSystemMessage', {
        type: 'success',
        message: 'Weekday prices copied.'
      })
    },
    async pasteWeekdayOnly() {
      const target = this.notExpiredPrices[this.currentItem]
      if (!target || target.status_id !== 1) {
        await this.$store.dispatch('setSystemMessage', {
          type: 'error',
          message: 'Switch to a draft schedule to paste.'
        })
        return
      }
      const payload = pricingClipboard.get('weekday')
      if (!payload?.length) {
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: 'Nothing to paste. Copy Weekday prices first.'
        })
        return
      }

      const scheduleId = target.id
      const replaceDraft = true
      this.pasteState.weekday = true
      try {
        if (replaceDraft && this.currentWeekday?.length) {
          const idsToDelete = this.currentWeekday.flatMap(row => row.ids || [])
          if (idsToDelete.length) {
            await merchantHelpers.deleteWeekdayPrices(
              this.$route.params.merchantId,
              scheduleId,
              { idNumbers: idsToDelete }
            )
          }
        }

        await Promise.all(
          payload.map(item =>
            merchantHelpers.createWeekdayPrices(
              this.$route.params.merchantId,
              scheduleId,
              item
            )
          )
        )

        await this.getPricingDetails(scheduleId)

        await this.$store.dispatch('setSystemMessage', {
          type: 'success',
          message: 'Weekday prices pasted to draft.'
        })
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: this.$t('remember_to_update_admin_v2_settings')
        })
      } catch (e) {
        console.error(e)
        await this.$store.dispatch('setSystemMessage', {
          type: 'error',
          message: 'Failed to paste Weekday prices.'
        })
      } finally {
        this.pasteState.weekday = false
      }
    },
    async copySequenceOnly() {
      const current = this.notExpiredPrices[this.currentItem]
      if (!current) return
      await this.getPricingDetails(current.id)
      const steps = this.mapSequence(this.priceDetails) || []
      const normalized = steps.map(s => this.normalizeSequenceStepForCreate(s))

      pricingClipboard.set('sequence', normalized, {
        currencyId: this.currencyId,
        sourceScheduleId: current.id,
        kind: 'sequence',
      })
      await this.$store.dispatch('setSystemMessage', { type: 'success', message: 'Sequence prices copied.' })
    },

    async copySequencePeriodsOnly() {
      const current = this.notExpiredPrices[this.currentItem]
      if (!current) return
      await this.getPricingDetails(current.id)
      await this.getSequencePricesPeriod()
      const normalized = (this.sequencePricePeriod || [])
        .map(p => this.normalizeSequencePeriodForCreate(p))

      pricingClipboard.set('sequencePeriods', normalized, {
        currencyId: this.currencyId,
        sourceScheduleId: current.id,
        kind: 'sequencePeriods',
      })

      await this.$store.dispatch('setSystemMessage', { type: 'success', message: 'Sequence periods copied.' })
    },

    async copyPeriodicOnly() {
      const current = this.notExpiredPrices[this.currentItem]
      if (!current) return
      await this.getPricingDetails(current.id)
      const per = this.mapPeriodic(this.priceDetails) || []
      const normalized = per.map(p => ({ time_period: p.time_period, price: p.price }))

      pricingClipboard.set('periodic', normalized, {
        currencyId: this.currencyId,
        sourceScheduleId: current.id,
        kind: 'periodic',
      })
      await this.$store.dispatch('setSystemMessage', { type: 'success', message: 'Periodic prices copied.' })
    },
    async pasteSequenceOnly() {
      const target = this.notExpiredPrices[this.currentItem]
      if (!target || target.status_id !== 1) {
        await this.$store.dispatch('setSystemMessage', { type: 'error', message: 'Switch to a draft schedule to paste.' })
        return
      }

      const payload = pricingClipboard.get('sequence')
      if (!payload?.length) {
        await this.$store.dispatch('setSystemMessage', { type: 'warning', message: 'Nothing to paste.' })
        return
      }
      this.pasteState.sequence = true
      const scheduleId = target.id
      try {
        if (this.currentSequence?.length) {
          const deletions = this.currentSequence.map(step =>
            merchantHelpers.deleteSequencePrices(this.$route.params.merchantId, scheduleId, step.id)
          )
          await Promise.all(deletions)
        }
        for (const step of payload) {
          await merchantHelpers.createSequencePrices(this.$route.params.merchantId, scheduleId, step)
        }
        await this.getPricingDetails(scheduleId)
        await this.$store.dispatch('setSystemMessage', { type: 'success', message: 'Sequence prices pasted.' })
        await this.$store.dispatch('setSystemMessage', { type: 'warning', message: this.$t('remember_to_update_admin_v2_settings') })
      } catch (e) {
        console.error(e)
        await this.$store.dispatch('setSystemMessage', { type: 'error', message: 'Failed to paste sequence.' })
      } finally {
        this.pasteState.sequence = false
      }
    },

    async pasteSequencePeriodsOnly() {
      const target = this.notExpiredPrices[this.currentItem]
      if (!target || target.status_id !== 1) {
        await this.$store.dispatch('setSystemMessage', { type: 'error', message: 'Switch to a draft schedule to paste.' })
        return
      }
      const payload = pricingClipboard.get('sequencePeriods')
      if (!payload?.length) {
        await this.$store.dispatch('setSystemMessage', { type: 'warning', message: 'Nothing to paste.' })
        return
      }
      this.pasteState.sequencePeriods = true
      const scheduleId = target.id
      try {
        await this.getPricingDetails(scheduleId)
        if (this.sequencePricePeriod?.length) {
          for (const group of this.sequencePricePeriod) {
            await this.deleteSequencePeriod(group)
          }
        }
        const seqId = this.priceDetails.sequencePriceSteps?.price_sequence_id
        for (const p of payload) {
          await merchantHelpers.createSequencePricesPeriod(
            this.$route.params.merchantId,
            scheduleId,
            seqId,
            p
          )
        }
        await this.getSequencePricesPeriod()
        await this.$store.dispatch('setSystemMessage', { type: 'success', message: 'Sequence periods pasted.' })
        await this.$store.dispatch('setSystemMessage', { type: 'warning', message: this.$t('remember_to_update_admin_v2_settings') })
      } catch (e) {
        console.error(e)
        await this.$store.dispatch('setSystemMessage', { type: 'error', message: 'Failed to paste sequence periods.' })
      } finally {
        this.pasteState.sequencePeriods = false
      }
    },

    async pastePeriodicOnly() {
      const target = this.notExpiredPrices[this.currentItem]
      if (!target || target.status_id !== 1) {
        await this.$store.dispatch('setSystemMessage', { type: 'error', message: 'Switch to a draft schedule to paste.' })
        return
      }
      const payload = pricingClipboard.get('periodic')
      if (!payload?.length) {
        await this.$store.dispatch('setSystemMessage', { type: 'warning', message: 'Nothing to paste.' })
        return
      }
      this.pasteState.periodic = true
      const scheduleId = target.id
      try {
        if (this.currentPeriodic?.length) {
          const deletions = this.currentPeriodic.map(item =>
            merchantHelpers.deletePeriodicPrices(this.$route.params.merchantId, scheduleId, item.id)
          )
          await Promise.all(deletions)
        }
        await Promise.all(
          payload.map(item =>
            merchantHelpers.createPeriodicPrices(this.$route.params.merchantId, scheduleId, item)
          )
        )
        await this.getPricingDetails(scheduleId)
        await this.$store.dispatch('setSystemMessage', { type: 'success', message: 'Periodic prices pasted.' })
        await this.$store.dispatch('setSystemMessage', { type: 'warning', message: this.$t('remember_to_update_admin_v2_settings') })
      } catch (e) {
        console.error(e)
        await this.$store.dispatch('setSystemMessage', { type: 'error', message: 'Failed to paste periodic.' })
      } finally {
        this.pasteState.periodic = false
      }
    },

    normalizeToHHmm(timeStr) {
      if (!timeStr) return '00:00'
      if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr)) return timeStr.slice(0, 5)
      if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr
      if (/^\d{2}$/.test(timeStr)) return `${timeStr}:00`
      const [h='00', m='00'] = String(timeStr).split(':')
      return `${h.padStart(2,'0')}:${m.padStart(2,'0')}`
    },
    normalizeWeekdayForCreate(groupedItem) {
      const map = (n) => ({
        mon: groupedItem.weekday.includes(1),
        tue: groupedItem.weekday.includes(2),
        wed: groupedItem.weekday.includes(3),
        thu: groupedItem.weekday.includes(4),
        fri: groupedItem.weekday.includes(5),
        sat: groupedItem.weekday.includes(6),
        sun: groupedItem.weekday.includes(7),
      })
      return {
        tod_start: groupedItem.tod_start,
        tod_end: groupedItem.tod_end,
        price: groupedItem.price,
        price_time_unit: { ...groupedItem.price_time_unit },     // { value, unit }
        charge_increment: { ...groupedItem.charge_increment },   // { value, unit }
        days_of_week: map(),
      }
    },
    normalizeSequenceStepForCreate(step) {
      const timePeriod = step.time_period || this.secondsToUnit(step.time_unit_seconds)

      const priceTimeUnit = step.ui_price_data
        ? step.ui_price_data.price_time_unit
        : step.price_time_unit || this.secondsToUnit(step.time_unit_seconds)

      const price = step.ui_price_data ? step.ui_price_data.price : step.price

      return {
        price,
        time_period: {
          value: timePeriod.value,
          unit: timePeriod.unit
        },
        price_time_unit: {
          value: priceTimeUnit.value,
          unit: priceTimeUnit.unit
        },
        charge_increment: {
          value: step.charge_increment.value,
          unit: step.charge_increment.unit
        },
        ui_price_data: step.ui_price_data
          ? {
              price: step.ui_price_data.price,
              price_time_unit: {
                value: step.ui_price_data.price_time_unit.value,
                unit: step.ui_price_data.price_time_unit.unit
              }
            }
          : undefined
      }
    },

    normalizeSequencePeriodForCreate(group) {
      return {
        time_of_day_start: this.toHHmm(group.time_of_day_start),
        time_of_day_end: this.toHHmm(group.time_of_day_end),
        days_of_week: [...group.days_of_week],
        applies_on_holiday: !!group.applies_on_holiday,
        parkings_check_level: group.parkings_check_level ?? 1,
      }
    },
    async editSchedule() {
      this.$emit('edit', this.priceDetails)
    },
    async openExpiredSchedule(schedule) {
      this.notExpiredPrices = this.notExpiredPrices.filter(
        (schedule) => !schedule.expired
      )
      this.notExpiredPrices.push(schedule)
      this.currentItem = this.notExpiredPrices.length - 1
      await this.getPricingDetails(schedule.id)
    },
    isActiveTab(tabIdx) {
      return this.currentItem === tabIdx
    },
    isInReview(item) {
      return item.status_id === 2
    },
    canPaste(type) {
      return pricingClipboard.has(type)
    },
    isDraft(item) {
      return item.status_id === 1
    },
    isActive(item) {
      return item.status_id === 3
    },
    onStorageChange(e) {
      if (e.key === 'pricingClipboard_v1') {
        this.$forceUpdate()
      }
    },
    isExpired(item) {
      return item.expired ?? false
    },
    formatScheduleDate(item) {
      const getDDMMFormat = (dateStr) => {
        const months = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ]
        const date = new Date(dateStr)
        return `${String(date.getDate()).padStart(2, '0')} ${
          months[date.getMonth()]
        }`
      }

      const getYYYYFormat = (dateStr) => {
        const date = new Date(dateStr)
        return date.getFullYear()
      }

      const occursFromDDMM = getDDMMFormat(item.occurs_from)
      const occursToDDMM = getDDMMFormat(item.occurs_to)
      const validFromYYYY = getYYYYFormat(item.valid_from)
      const validToYYYY = item.valid_to ? getYYYYFormat(item.valid_to) : null
      let result = ''
      if (item.priority === 1) {
        result += 'Priority '
      }
      result += `${occursFromDDMM} - ${occursToDDMM} (${validFromYYYY}`
      if (validToYYYY) {
        result += validFromYYYY !== validToYYYY ? `-${validToYYYY}` : ''
      } else {
        result += ' ➔'
      }
      result += ')'

      return result
    },
    async getPricingDetails(scheduleId) {
      const price = await merchantHelpers.getPricingScheduleDetails(
        this.$route.params.merchantId,
        scheduleId
      )
      this.priceDetails = price
      this.currentWeekday = this.groupWeekdays(price)
      this.currentPeriodic = this.mapPeriodic(price)
      this.currentSequence = this.mapSequence(price)
    },
    secondsToUnit(seconds) {
      if (seconds % (24 * 60 * 60) === 0) {
        return { value: seconds / (24 * 60 * 60), unit: 'day' }
      } else if (seconds % (60 * 60) === 0) {
        return { value: seconds / (60 * 60), unit: 'hour' }
      } else if (seconds % 60 === 0) {
        return { value: seconds / 60, unit: 'minute' }
      } else {
        return { value: seconds, unit: 'second' }
      }
    },
    groupWeekdays(price) {
      if (!price?.weekdayPaidTimes) {
        return []
      }
      const weekdaysGrouped = price.weekdayPaidTimes.reduce((acc, curr) => {
        const existing = acc.find(
          (item) =>
            item.tod_start === curr.todStart &&
            item.tod_end === curr.todEnd &&
            item.price === curr.basePrice.price
        )

        if (existing) {
          existing.weekday.push(curr.dowStart)
          existing.ids.push(curr.id)
        } else {
          acc.push({
            tod_start: curr.todStart,
            tod_end: curr.todEnd,
            price: curr.basePrice.price,
            weekday: [curr.dowStart],
            ids: [curr.id],
            charge_increment: this.secondsToUnit(
              curr.basePrice.charge_increment_seconds
            ),
            price_time_unit: this.secondsToUnit(
              curr.basePrice.price_time_unit_seconds
            )
          })
        }
        if (curr.dowEnd - curr.dowStart > 1) {
          const daysList = []
          for (let i = curr.dowStart; i <= curr.dowEnd; i++) {
            daysList.push(i)
          }
          acc[acc.length - 1].weekday = daysList
        }

        return acc
      }, [])

      return weekdaysGrouped
    },
    mapPeriodic(price) {
      if (!price?.periodicPriceItems) {
        return []
      }
      return price.periodicPriceItems.items.map((item) => {
        return {
          id: item.id,
          time_period: this.secondsToUnit(item.time_amount_seconds),
          price: item.price
        }
      })
    },
    mapSequence(price) {
      if (!price?.sequencePriceSteps) {
        return []
      }
      return price.sequencePriceSteps.steps.map((step) => {
        return {
          id: step.id,
          step_index: step.step_index,
          price: step.ui_price_data ? step.ui_price_data.price : step.price,
          price_time_unit: step.ui_price_data
            ? step.ui_price_data.price_time_unit
            : this.secondsToUnit(step.time_unit_seconds),
          charge_increment: this.secondsToUnit(step.charge_increment_seconds),
          time_period: this.secondsToUnit(step.time_unit_seconds),
          ...step
        }
      })
    },
    async addWeekDay(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem]?.id
      const weekdayPrice = await merchantHelpers.createWeekdayPrices(
        this.$route.params.merchantId,
        scheduleId,
        item
      )
      if (weekdayPrice) {
        await this.getPricingDetails(scheduleId)
        this.closeDataTableWeekdayPopup = !this.closeDataTableWeekdayPopup
        await this.$store.dispatch('setSystemMessage', {
          type: 'success',
          message: this.$t('added_confirm_message', { object: 'Weekday price' })
        })
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: this.$t('remember_to_update_admin_v2_settings')
        })
      }
    },
    async deleteWeekDay(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      await merchantHelpers.deleteWeekdayPrices(
        this.$route.params.merchantId,
        scheduleId,
        item
      )
      await this.getPricingDetails(scheduleId)
      await this.$store.dispatch('setSystemMessage', {
        type: 'delete',
        message: this.$t('price_is_removed', { object: 'Weekday price' })
      })
      await this.$store.dispatch('setSystemMessage', {
        type: 'warning',
        message: this.$t('remember_to_update_admin_v2_settings')
      })
    },
    async addSequence(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      await merchantHelpers.createSequencePrices(
        this.$route.params.merchantId,
        scheduleId,
        item
      )
      await this.getPricingDetails(scheduleId)
      await this.$store.dispatch('setSystemMessage', {
        type: 'success',
        message: this.$t('added_confirm_message', { object: 'Sequence price' })
      })
      await this.$store.dispatch('setSystemMessage', {
        type: 'warning',
        message: this.$t('remember_to_update_admin_v2_settings')
      })
    },
    groupSequencePeriods(data) {
      const groups = {};

      data.forEach(item => {
        const key = [
          item.time_of_day_start,
          item.time_of_day_end,
          item.parkings_check_level,
          item.applies_on_holiday,
          item.schedule_id,
          item.sequence_id
        ].join('|');

        if (!groups[key]) {
          groups[key] = {
            period_ids: [],
            schedule_id: item.schedule_id,
            sequence_id: item.sequence_id,
            time_of_day_start: item.time_of_day_start,
            time_of_day_end: item.time_of_day_end,
            parkings_check_level: item.parkings_check_level,
            applies_on_holiday: item.applies_on_holiday,
            days_of_week: []
          };
        }

        groups[key].period_ids.push(item.id);
        groups[key].days_of_week.push(item.day_of_week_start);
      });

      Object.values(groups).forEach(group => {
        group.period_ids.sort((a, b) => a - b);
        group.days_of_week.sort((a, b) => a - b);
      });
        this.sequencePricePeriod = Object.values(groups)
    },
    async getSequencePricesPeriod() {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      const sequencePriceId = this.priceDetails.sequencePriceSteps?.price_sequence_id ?? null
      if (sequencePriceId) {
        const periods = await merchantHelpers.getSequencePricesPeriod(
          this.$route.params.merchantId,
          scheduleId,
          sequencePriceId
        )
        this.groupSequencePeriods(periods)
      } else {
        this.groupSequencePeriods([])
      }
    },
    async createSequencePricesPeriod(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      const sequencePriceId = this.priceDetails.sequencePriceSteps.price_sequence_id
      await merchantHelpers.createSequencePricesPeriod(
        this.$route.params.merchantId,
        scheduleId,
        sequencePriceId,
        item
      )
      await this.getSequencePricesPeriod()
      await this.$store.dispatch('setSystemMessage', {
        type: 'success',
        message: this.$t('added_confirm_message', { object: 'Sequence price period' })
      })
    },
    async deleteSequencePeriod(item) {
      const ids = item.period_ids
      const scheduleId = item.schedule_id
      const sequenceId = item.sequence_id

      const requests = ids.map(periodId => {
        return merchantHelpers.deleteSequencePricesPeriod(
          this.$route.params.merchantId,
          scheduleId,
          sequenceId,
          periodId
        )
      })
        try {
        await Promise.all(requests)
        await this.fetchAllowedTime()
      } catch (error) {
        console.error('One or more requests failed:', error)
      }
      await this.$store.dispatch('setSystemMessage', {
        type: 'error',
        message: 'Sequence price deleted'
      })
      await this.getSequencePricesPeriod()
    },
    async updateSequencePeriod(item) {
      const scheduleId = item.schedule_id
      const sequenceId = item.sequence_id
      
      const periods = await merchantHelpers.updateSequencePricesPeriod(
        this.$route.params.merchantId,
        scheduleId,
        sequenceId,
        item
      )
      this.groupSequencePeriods(periods)
      await this.$store.dispatch('setSystemMessage', {
        type: 'success',
        message: 'Sequence price period updated'
      })
    },
    async updateWeekDay(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      const idNumbers = { idNumbers: item.ids }
      await merchantHelpers.deleteWeekdayPrices(
        this.$route.params.merchantId,
        scheduleId,
        idNumbers
      )
      const weekdayPrice = await merchantHelpers.createWeekdayPrices(
        this.$route.params.merchantId,
        scheduleId,
        item
      )
      if (weekdayPrice) {
        await this.getPricingDetails(scheduleId)
        this.closeDataTableWeekdayPopup = !this.closeDataTableWeekdayPopup
        await this.$store.dispatch('setSystemMessage', {
          type: 'success',
          message: this.$t('changes_are_saved')
        })
        await this.$store.dispatch('setSystemMessage', {
          type: 'warning',
          message: this.$t('remember_to_update_admin_v2_settings')
        })
      }
    },
    async updateSequence(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      await merchantHelpers.updateSequencePrices(
        this.$route.params.merchantId,
        scheduleId,
        item.id,
        item
      )
      await this.getPricingDetails(scheduleId)
      await this.$store.dispatch('setSystemMessage', {
        type: 'success',
        message: this.$t('changes_are_saved')
      })
      await this.$store.dispatch('setSystemMessage', {
        type: 'warning',
        message: this.$t('remember_to_update_admin_v2_settings')
      })
    },
    async deleteSequence(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      await merchantHelpers.deleteSequencePrices(
        this.$route.params.merchantId,
        scheduleId,
        item
      )
      await this.getPricingDetails(scheduleId)
      await this.$store.dispatch('setSystemMessage', {
        type: 'delete',
        message: this.$t('price_is_removed', { object: 'Sequence price' })
      })
      await this.$store.dispatch('setSystemMessage', {
        type: 'warning',
        message: this.$t('remember_to_update_admin_v2_settings')
      })
    },
    async addPeriodic(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      await merchantHelpers.createPeriodicPrices(
        this.$route.params.merchantId,
        scheduleId,
        item
      )
      await this.getPricingDetails(scheduleId)
      await this.$store.dispatch('setSystemMessage', {
        type: 'success',
        message: this.$t('added_confirm_message', { object: 'Periodic price' })
      })
      await this.$store.dispatch('setSystemMessage', {
        type: 'warning',
        message: this.$t('remember_to_update_admin_v2_settings')
      })
    },
    async updatePeriodic(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      await merchantHelpers.updatePeriodicPrices(
        this.$route.params.merchantId,
        scheduleId,
        item.id,
        item
      )
      await this.getPricingDetails(scheduleId)
      await this.$store.dispatch('setSystemMessage', {
        type: 'success',
        message: this.$t('changes_are_saved')
      })
      await this.$store.dispatch('setSystemMessage', {
        type: 'warning',
        message: this.$t('remember_to_update_admin_v2_settings')
      })
    },
    async deletePeriodic(item) {
      const scheduleId = this.notExpiredPrices[this.currentItem].id
      await merchantHelpers.deletePeriodicPrices(
        this.$route.params.merchantId,
        scheduleId,
        item
      )
      await this.getPricingDetails(scheduleId)
      await this.$store.dispatch('setSystemMessage', {
        type: 'delete',
        message: this.$t('price_is_removed', { object: 'Periodic price' })
      })
      await this.$store.dispatch('setSystemMessage', {
        type: 'warning',
        message: this.$t('remember_to_update_admin_v2_settings')
      })
    },
    setPrices() {
      this.expiredPrices = []
      this.notExpiredPrices = []
      this.prices.forEach((price) => {
        price.valid_to && moment(price.valid_to).isBefore(moment(new Date()))
          ? this.expiredPrices.push({ expired: true, ...price })
          : this.notExpiredPrices.push({ ...price })
        if (price.id === this.priceDetails?.schedule[0]?.id) {
          this.priceDetails = { ...this.priceDetails, schedule: [price] }
        }
      })
      if (!this.prices.length) {
        this.priceDetails = {}
      }
    },
    checkExpired(item) {
      return item.expired ? 'Past ' : ''
    },
    toHHmm(timeStr) {
      if (!timeStr) return timeStr
      const parts = String(timeStr).split(':')

      const h = (parts[0] || '00').padStart(2, '0')
      const m = (parts[1] || '00').padStart(2, '0')
      const s = (parts[2] || '00').padStart(2, '0')
      return `${h}:${m}:${s}`
    },
  },
  watch: {
    async currentItem(newValue) {
      if (newValue !== undefined) {
        if (!this.notExpiredPrices[newValue]?.expired) {
          this.notExpiredPrices = this.notExpiredPrices.filter(
            (price) => !price.expired
          )
        }
        
        this.$emit('priceItemChanged', newValue)
        if (this.notExpiredPrices.length >= newValue) {
          this.currentWeekday = []
          this.currentPeriodic = []
          this.currentSequence = []
          await this.getPricingDetails(this.notExpiredPrices[newValue].id)
          await this.getSequencePricesPeriod()
        }
      }
    },
    prices() {
      this.setPrices()
      if (
        this.notExpiredPrices.length &&
        this.notExpiredPrices.length >= this.currentItem
      ) {
        this.getPricingDetails(this.notExpiredPrices[this.currentItem].id)
      }
    },
    priceDetails(newValue) {
      this.$emit('pricigScheduleList', newValue)
    }
  },
  async mounted() {
    window.addEventListener('storage', this.onStorageChange)
    if (this.prices.length) {
      this.setPrices()
      if (
        this.notExpiredPrices.length &&
        this.notExpiredPrices.length >= this.currentItem
      ) {
        await this.getPricingDetails(this.notExpiredPrices[this.currentItem].id)
        await this.getSequencePricesPeriod()
      }
    }
  },
  beforeDestroy() {
   window.removeEventListener('storage', this.onStorageChange)
  },
}
</script>

<style lang="scss" scoped>
@import '~@/style/common';
@import '~@/style/variables';

.tabs {
  margin-left: 0;
  margin-top: 0;
  max-height: 32px;
}

.tab {
  font-size: 10px;
}

.btn-past-schedule {
  @include text-label;
  padding-right: 0 !important;
  margin-top: 4px;
  text-transform: unset !important;
}

.tab-item {
  overflow-y: auto;
  transition: false;
}

.has-review {
  color: $color-warning;
}

.tooltip-content {
  font-size: 10px;
  font-weight: 400;
  line-height: 1.2;
  width: 168px;
  background-color: white;
  color: black;
  padding: 8px 16px;
  text-align: center;
  border-radius: 4px;
  position: relative;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
}
</style>
<style lang="scss">
.v-tabs {
  .btn-past-schedule {
    display: flex;
    align-items: flex-end;
    padding-bottom: 4px !important;
    span {
      font-family: Roboto, sans-serif;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 130%;
    }
  }
}

.tab-pricing-wrapper {
  display: flex;
  align-items: center;
}
.tab-pricing-items {
  .tab-item {
    overflow-y: visible;
  }
}
</style>
